// src/pages/DashboardPage.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useSDK } from '../context/SDKContext.tsx';
import type { FinopsKpiData } from '@assistia/sdk';
import { CheckCircle2, AlertTriangle } from 'lucide-react';

export default function DashboardPage(): React.ReactElement {
  const { sdk, isSimulated } = useSDK();
  const [data, setData] = useState<FinopsKpiData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: kpi } = await sdk.finops.getFinopsKpi();
      setData(kpi);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [sdk]);

  useEffect(() => { void load(); }, [load]);

  const cbState = sdk.circuitBreaker?.getState?.() ?? 'Closed';
  const isDegraded = cbState === 'Open';

  const Badge = () => (
    <span className="inline-flex items-center gap-1 text-sm font-medium">
      {isDegraded
        ? <AlertTriangle size={12} className="text-orange-500" />
        : <CheckCircle2 size={12} className="text-green-600" />}
      {isDegraded ? '⚠ Degrade' : '✓ Nominal'}
      {isSimulated && <span className="ml-2 text-orange-500 text-xs">(simulation)</span>}
    </span>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Tableau de bord</h1>
          <p className="text-sm text-muted-foreground mt-1">KPIs FinOps</p>
        </div>
        <button
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-border hover:bg-muted disabled:opacity-50 transition-colors"
        >
          Actualiser
        </button>
      </div>

      <Badge />

      {loading && <div className="text-muted-foreground text-sm">Chargement...</div>}
      {error && <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">{error}</div>}

      {!loading && data && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="p-4 border border-border rounded-lg bg-card">
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Dossiers traites</div>
            <div className="text-2xl font-bold">{data.total_dossiers}</div>
          </div>
          <div className="p-4 border border-border rounded-lg bg-card">
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Cout moyen / dossier</div>
            <div className="text-2xl font-bold">{data.avg_cost_eur.toFixed(4)} EUR</div>
            <div className="text-xs text-muted-foreground mt-1">cible &lt; 0.20 EUR</div>
          </div>
          <div className="p-4 border border-border rounded-lg bg-card">
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Temps moyen</div>
            <div className="text-2xl font-bold">
              {data.avg_duration_ms >= 1000
                ? (data.avg_duration_ms / 1000).toFixed(1) + ' s'
                : data.avg_duration_ms + ' ms'}
            </div>
            <div className="text-xs text-muted-foreground mt-1">cible &lt; 2 min</div>
          </div>
          <div className="p-4 border border-border rounded-lg bg-card">
            <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Taux de succes</div>
            <div className="text-2xl font-bold">{data.success_rate_pct.toFixed(1)} %</div>
            <div className="text-xs text-muted-foreground mt-1">cible &gt; 95 %</div>
          </div>
        </div>
      )}
    </div>
  );
}
